import React, { useMemo, useState } from 'react';
import { App, Button, Card, Col, Descriptions, Dropdown, Row, Space, Spin, Table, Tabs, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  MoreOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { ENTITY_TYPE_HR_MANPOWER_REQUISITION } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import { fmtDate, recruitErrorMessage, stageLabel, useRecruitAccess } from './shared/recruitUi';

const { Text } = Typography;

const RequisitionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { canView, canManage } = useRecruitAccess();
  const [loading, setLoading] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<'cancel' | null>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.requisition(id || ''),
    queryFn: () => hrmsService.getRequisition(id!),
    enabled: Boolean(id),
  });

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(['Draft', 'PendingApproval', 'Open', 'Filled'], row?.status || 'Draft', {
        rejectedStatuses: ['Rejected', 'Cancelled'],
      }),
    [row?.status]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.requisition(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.requisitions() });
  };

  const run = async (key: string, fn: () => Promise<any>, okMsg: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(okMsg);
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Requisition" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Requisition" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const editable = canManage && ['Draft', 'Rejected', 'PendingApproval', 'Open', 'OnHold'].includes(row.status);
  const moreItems = !canManage ? [] : [
    row.status === 'Open' && { key: 'hold', label: 'Put on hold' },
    row.status === 'OnHold' && { key: 'resume', label: 'Resume hiring' },
    ['Draft', 'Rejected', 'Open', 'OnHold'].includes(row.status) && { key: 'cancel', label: 'Cancel requisition', danger: true },
  ].filter(Boolean) as any[];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Manpower requisition"
          documentNumber={row.requisition_number || '—'}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{stageLabel(row.status)}</Tag>}
          subtitle={<Text type="secondary">{row.job_title} · {row.department_name || '—'}</Text>}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(canView ? '/hr/requisitions' : '/home/approvals')}>
                Back
              </Button>
              {editable && (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/requisitions/${id}/edit`)}>
                  Edit
                </Button>
              )}
              {canManage && ['Draft', 'Rejected'].includes(row.status) && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={loading === 'submit'}
                  onClick={() => run('submit', () => hrmsService.submitRequisition(id!), 'Submitted for approval')}
                >
                  Submit
                </Button>
              )}
              {canManage && row.status === 'Open' && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/hr/jobs/new?requisition_id=${row.id}`)}>
                  Create job posting
                </Button>
              )}
              {moreItems.length > 0 && (
                <Dropdown
                  menu={{
                    items: moreItems,
                    onClick: ({ key }) => {
                      if (key === 'hold') run('hold', () => hrmsService.holdRequisition(id!, true), 'Requisition on hold');
                      if (key === 'resume') run('resume', () => hrmsService.holdRequisition(id!, false), 'Hiring resumed');
                      if (key === 'cancel') setPrompt('cancel');
                    },
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Tabs
            items={[
              {
                key: 'summary',
                label: 'Summary',
                children: (
                  <>
                    <Card style={{ marginBottom: 16 }}>
                      <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                        <Descriptions.Item label="Department">{row.department_name || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Openings">
                          {row.filled_count ?? 0} filled of {row.headcount}
                        </Descriptions.Item>
                        <Descriptions.Item label="Employment type">{row.employment_type || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Hiring type">{row.hiring_type || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Urgency">{row.urgency || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Location">{row.location_text || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Recruiter">{row.recruiter_name || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Hiring manager">{row.hiring_manager_name || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Min experience">
                          {row.min_experience_years != null ? `${row.min_experience_years} yr` : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Target join">{fmtDate(row.target_join_date)}</Descriptions.Item>
                        <Descriptions.Item label="Budget (monthly)">
                          {row.budget_min != null || row.budget_max != null
                            ? `${row.currency || 'AED'} ${row.budget_min ?? '—'} – ${row.budget_max ?? '—'}`
                            : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cost centre">{row.cost_center_code || '—'}</Descriptions.Item>
                        <Descriptions.Item label="Required skills" span={2}>
                          {row.required_skills || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Justification" span={2}>
                          {row.justification || '—'}
                        </Descriptions.Item>
                        {row.rejection_reason_code && (
                          <Descriptions.Item label="Rejection" span={2}>
                            <Text type="danger">
                              {row.rejection_reason_code}
                              {row.rejection_notes ? ` — ${row.rejection_notes}` : ''}
                            </Text>
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Notes" span={2}>
                          <span style={{ whiteSpace: 'pre-wrap' }}>{row.notes || '—'}</span>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                    <EntityAttachmentsCard
                      entityType="HR_MPR"
                      entityId={row.id}
                      title="Requisition attachments"
                      uploadHint="Org chart, budget approval, JD draft, replacement memo"
                    />
                  </>
                ),
              },
              {
                key: 'jobs',
                label: `Job postings (${row.jobs?.length || 0})`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={row.jobs || []}
                    columns={[
                      { title: 'Job #', dataIndex: 'job_number', render: (v, j: any) => <a onClick={() => navigate(`/hr/jobs/${j.id}`)}>{v}</a> },
                      { title: 'Title', dataIndex: 'title' },
                      { title: 'Applications', dataIndex: 'applications', width: 110 },
                      { title: 'Filled', key: 'f', width: 90, render: (_, j: any) => `${j.filled_count}/${j.openings}` },
                      { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={hrmsStatusColor(s)}>{s}</Tag> },
                    ]}
                  />
                ),
              },
              {
                key: 'hires',
                label: `Hires (${row.hires?.length || 0})`,
                children: (
                  <Table
                    rowKey="offer_id"
                    size="small"
                    pagination={false}
                    dataSource={row.hires || []}
                    columns={[
                      { title: 'Employee', dataIndex: 'full_name' },
                      { title: 'Employee #', dataIndex: 'employee_number' },
                      { title: 'Join date', dataIndex: 'join_date', render: (v) => fmtDate(v) },
                      {
                        title: 'Offer',
                        dataIndex: 'offer_number',
                        render: (v, h: any) => <a onClick={() => navigate(`/hr/offers/${h.offer_id}`)}>{v}</a>,
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_MANPOWER_REQUISITION}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.requisition(id!), hrmsKeys.requisitions()]}
            />
          )}
        </Col>
      </Row>

      <ReasonPrompt
        open={prompt === 'cancel'}
        title="Cancel requisition"
        okText="Cancel requisition"
        notesRequired
        notesLabel="Reason"
        onCancel={() => setPrompt(null)}
        onSubmit={async (v) => {
          await run('cancel', () => hrmsService.cancelRequisition(id!, v.notes || ''), 'Requisition cancelled');
          setPrompt(null);
        }}
      />
    </WorkspaceLayout>
  );
};

export default RequisitionDetailPage;
